import { useEffect, useMemo } from 'react';

import {
	type BaseAIAction,
	type BaseAISmartButtonContext,
	type BaseAutomationsConfig,
	useAISmartButtonActions,
} from '../../ai-smart-button-store';

/**
 * Hook for managing actions associated with a specific source.
 * Automatically handles cleanup on unmount.
 */
export const useSourceAIActions = (sourceId: string) => {
	const {
		removeContext,
		removeAll,
		removeAutomationsConfig,
		setActions,
		setNudges,
		setSuggestedActions,
		setContext,
		setAutomationsConfig,
	} = useAISmartButtonActions();

	useEffect(() => {
		// Cleanup on unmount.
		return () => {
			removeAll(sourceId);
			removeContext(sourceId);
			removeAutomationsConfig(sourceId);
		};
	}, [sourceId, removeAll, removeContext, removeAutomationsConfig]);

	return useMemo(
		() => ({
			/**
			 * Replaces all actions for this source with new ones.
			 */
			setActions: (actions: BaseAIAction[]) => {
				setActions(sourceId, actions);
			},

			/*
			 * Replaces all nudges for this source with new ones.
			 */
			setNudges: (nudges: BaseAIAction[]) => {
				setNudges(sourceId, nudges);
			},

			/**
			 * Replaces all suggested actions for this source with new ones.
			 */
			setSuggestedActions: (suggestedActions: BaseAIAction[]) => {
				setSuggestedActions(sourceId, suggestedActions);
			},

			/**
			 * Sets context for this source.
			 */
			setContext: (context: BaseAISmartButtonContext) => setContext({ ...context, sourceId }),

			/**
			 * Sets automations config for this source.
			 */
			setAutomationsConfig: (automationsConfig: BaseAutomationsConfig) =>
				setAutomationsConfig({ ...automationsConfig, sourceId }),

			/**
			 * Removes context for this source.
			 */
			removeContext: () => removeContext(sourceId),

			/**
			 * Removes automations config for this source.
			 */
			removeAutomationsConfig: () => removeAutomationsConfig(sourceId),

			/**
			 * Removes all actions for this source.
			 */
			removeAll: () => removeAll(sourceId),
		}),
		[
			sourceId,
			removeAll,
			removeContext,
			removeAutomationsConfig,
			setActions,
			setNudges,
			setSuggestedActions,
			setContext,
			setAutomationsConfig,
		],
	);
};

export type SourceAIActions = ReturnType<typeof useSourceAIActions>;
